import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressContactsCoreApiV4ContactsServiceV4Url(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www._base_domain_': [
      {
        srcPath: '/_api/contacts/v4/contacts',
        destPath: '/v4/contacts',
      },
      {
        srcPath: '/_api/contacts/v4/bulk',
        destPath: '/v4/bulk',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/contacts/v4/contacts',
        destPath: '/v4/contacts',
      },
      {
        srcPath: '/contacts/v4/bulk',
        destPath: '/v4/bulk',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/contacts/v4/contacts',
        destPath: '/v4/contacts',
      },
      {
        srcPath: '/_api/contacts/v4/bulk',
        destPath: '/v4/bulk',
      },
    ],
    'manage.nownia.com': [
      {
        srcPath: '/_api/contacts/v4/contacts',
        destPath: '/v4/contacts',
      },
      {
        srcPath: '/_api/contacts/v4/bulk',
        destPath: '/v4/bulk',
      },
    ],
    _: [
      {
        srcPath: '/_api/contacts/v4/bulk',
        destPath: '/v4/bulk',
      },
      {
        srcPath: '/_api/contacts/v4/contacts',
        destPath: '/v4/contacts',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/contacts/v4/bulk',
        destPath: '/v4/bulk',
      },
      {
        srcPath: '/_api/contacts/v4/contacts',
        destPath: '/v4/contacts',
      },
    ],
    'payments.base44.com': [
      {
        srcPath: '/_api/contacts/v4/contacts',
        destPath: '/v4/contacts',
      },
      {
        srcPath: '/_api/contacts/v4/bulk',
        destPath: '/v4/bulk',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_crm_contacts';

/**
 * Creates a new contact.
 *
 * The `info` object must include at least one of the following:
 * - Name
 * - Phone number
 * - Email address
 *
 * By default, if the call contains an email already in use by another contact,
 * the new contact won't be created. To override this behavior, set `allowDuplicates` to `true`.
 */
export function createContact(payload: object): RequestOptionsFactory<any> {
  function __createContact({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'info.addresses.items.address.geocode.latitude' },
          { path: 'info.addresses.items.address.geocode.longitude' },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'info.picture.image.urlExpirationDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.contacts.v4.contact',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.contacts.core.api.v4.ContactsServiceV4.CreateContact',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressContactsCoreApiV4ContactsServiceV4Url({
        protoPath: '/v4/contacts',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'contact.createdDate' },
              { path: 'contact.updatedDate' },
              { path: 'contact.lastActivity.activityDate' },
              { path: 'contact.lastActivity.date' },
              { path: 'contact.picture.urlExpirationDate' },
              { path: 'contact.info.picture.image.urlExpirationDate' },
              { path: 'contact.memberInfo.signupDate' },
              {
                path: 'contact.memberInfo.profileInfo.photo.urlExpirationDate',
              },
              { path: 'contact.memberInfo.sessionInfo.lastWebLogin' },
              { path: 'contact.memberInfo.sessionInfo.lastMobileLogin' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'contact.info.addresses.items.address.geocode.latitude' },
              {
                path: 'contact.info.addresses.items.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createContact;
}

/**
 * Updates a contact.
 *
 * Each time the contact is updated,
 * `revision` increments by 1.
 * The existing `revision` must be included when updating the contact.
 * This ensures you're working with the latest contact information,
 * and it prevents unintended overwrites.
 *
 * You can't call Update Contact to update the `primaryEmail` of a contact who is also a member.
 * Instead, use the Members API to update the member's login email, which will also update the contact's primary email.
 *
 * Members are typically linked to contacts, and while they share a relationship, the member ID and contact ID are distinct identifiers. Make sure to specify the contact ID when calling Update Contact.
 */
export function updateContact(payload: object): RequestOptionsFactory<any> {
  function __updateContact({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'info.addresses.items.address.geocode.latitude' },
          { path: 'info.addresses.items.address.geocode.longitude' },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'info.picture.image.urlExpirationDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.contacts.v4.contact',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.contacts.core.api.v4.ContactsServiceV4.UpdateContact',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressContactsCoreApiV4ContactsServiceV4Url({
        protoPath: '/v4/contacts/{contactId}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'contact.createdDate' },
              { path: 'contact.updatedDate' },
              { path: 'contact.lastActivity.activityDate' },
              { path: 'contact.lastActivity.date' },
              { path: 'contact.picture.urlExpirationDate' },
              { path: 'contact.info.picture.image.urlExpirationDate' },
              { path: 'contact.memberInfo.signupDate' },
              {
                path: 'contact.memberInfo.profileInfo.photo.urlExpirationDate',
              },
              { path: 'contact.memberInfo.sessionInfo.lastWebLogin' },
              { path: 'contact.memberInfo.sessionInfo.lastMobileLogin' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'contact.info.addresses.items.address.geocode.latitude' },
              {
                path: 'contact.info.addresses.items.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateContact;
}

/**
 * Merges source contacts into a target contact.
 *
 * Merging contacts has the following effects on the target contact:
 *
 * - No target contact data is overwritten or deleted.
 * - Arrays (emails, phone numbers, addresses, and labels) from the source contacts are added to the target contact's arrays.
 * - If you merge more than one source contact, the 1st source contact specified is added first, then the 2nd, and so on.
 * - If there is duplicate information between the target contact and the source contact, the information isn't duplicated in the target contact's arrays.
 *
 * <blockquote class="important">
 *
 * __Important:__
 * Merges cannot be undone.
 * In REST, call Preview Merge Contacts to test before merging.
 *
 * </blockquote>
 *
 * When you merge contacts, source contacts are typically deleted.
 * However, if a source contact is a site member or a Wix user,
 * the merge will fail because site members and Wix users can't be deleted.
 * This means that site members and Wix users can only be specified as target contacts in a merge.
 *
 * After merging, calling Get Contact with a deleted source contact ID returns the target contact ID.
 * Specifying a deleted source contact ID is not supported on any other method.
 *
 * When contacts are merged:
 *
 * - The Contact Merged event is triggered.
 * - The Contact Updated event is triggered for the target contact. `originatedFrom` is set to `merge`.
 * - The Contact Deleted event is triggered for each source contact. `originatedFrom` is set to `merge`.
 */
export function mergeContacts(payload: object): RequestOptionsFactory<any> {
  function __mergeContacts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.contacts.v4.contact',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.contacts.core.api.v4.ContactsServiceV4.MergeContacts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressContactsCoreApiV4ContactsServiceV4Url({
        protoPath: '/v4/contacts/{targetContactId}/merge',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'contact.createdDate' },
              { path: 'contact.updatedDate' },
              { path: 'contact.lastActivity.activityDate' },
              { path: 'contact.lastActivity.date' },
              { path: 'contact.picture.urlExpirationDate' },
              { path: 'contact.info.picture.image.urlExpirationDate' },
              { path: 'contact.memberInfo.signupDate' },
              {
                path: 'contact.memberInfo.profileInfo.photo.urlExpirationDate',
              },
              { path: 'contact.memberInfo.sessionInfo.lastWebLogin' },
              { path: 'contact.memberInfo.sessionInfo.lastMobileLogin' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'contact.info.addresses.items.address.geocode.latitude' },
              {
                path: 'contact.info.addresses.items.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __mergeContacts;
}

/**
 * Previews merging source contacts into a target contact.
 *
 * This method performs a dry run without deleting or updating any contacts.
 * A preview of the updated target contact is returned,
 * but no data is changed in the contact list.
 *
 * To perform the actual merge, use Merge Contacts.
 */
export function previewMergeContacts(
  payload: object
): RequestOptionsFactory<any> {
  function __previewMergeContacts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.contacts.v4.contact',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.contacts.core.api.v4.ContactsServiceV4.PreviewMergeContacts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressContactsCoreApiV4ContactsServiceV4Url({
        protoPath: '/v4/contacts/{targetContactId}/preview-merge',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'contact.createdDate' },
              { path: 'contact.updatedDate' },
              { path: 'contact.lastActivity.activityDate' },
              { path: 'contact.lastActivity.date' },
              { path: 'contact.picture.urlExpirationDate' },
              { path: 'contact.info.picture.image.urlExpirationDate' },
              { path: 'contact.memberInfo.signupDate' },
              {
                path: 'contact.memberInfo.profileInfo.photo.urlExpirationDate',
              },
              { path: 'contact.memberInfo.sessionInfo.lastWebLogin' },
              { path: 'contact.memberInfo.sessionInfo.lastMobileLogin' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'contact.info.addresses.items.address.geocode.latitude' },
              {
                path: 'contact.info.addresses.items.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __previewMergeContacts;
}

/**
 * Deletes a contact.
 *
 * Deleting a contact permanently removes it from the contact list.
 * If a contact is also a site member or Wix user, or has a valid billing subscription,
 * the contact can't be deleted. The related site member or Wix user must first be deleted
 * and any valid billing subscriptions must be canceled, before the contact can be deleted.
 *
 * Members are typically linked to contacts, and while they share a relationship, the member ID and contact ID are distinct identifiers.
 * Make sure to specify the contact ID when calling Delete Contact.
 */
export function deleteContact(payload: object): RequestOptionsFactory<any> {
  function __deleteContact({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.contacts.v4.contact',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.contacts.core.api.v4.ContactsServiceV4.DeleteContact',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressContactsCoreApiV4ContactsServiceV4Url({
        protoPath: '/v4/contacts/{contactId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteContact;
}

/**
 * Adds labels to a contact.
 *
 * Use the Labels API to create or retrieve labels.
 *
 * Members are typically linked to contacts, and while they share a relationship, the member ID and contact ID are distinct identifiers.
 * Make sure to specify the contact ID when calling Label Contact.
 */
export function labelContact(payload: object): RequestOptionsFactory<any> {
  function __labelContact({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.contacts.v4.contact',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.contacts.core.api.v4.ContactsServiceV4.LabelContact',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressContactsCoreApiV4ContactsServiceV4Url({
        protoPath: '/v4/contacts/{contactId}/labels',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'contact.createdDate' },
              { path: 'contact.updatedDate' },
              { path: 'contact.lastActivity.activityDate' },
              { path: 'contact.lastActivity.date' },
              { path: 'contact.picture.urlExpirationDate' },
              { path: 'contact.info.picture.image.urlExpirationDate' },
              { path: 'contact.memberInfo.signupDate' },
              {
                path: 'contact.memberInfo.profileInfo.photo.urlExpirationDate',
              },
              { path: 'contact.memberInfo.sessionInfo.lastWebLogin' },
              { path: 'contact.memberInfo.sessionInfo.lastMobileLogin' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'contact.info.addresses.items.address.geocode.latitude' },
              {
                path: 'contact.info.addresses.items.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __labelContact;
}

/**
 * Removes labels from a contact.
 *
 * If a label is no longer needed
 * and you want to remove it from all contacts,
 * you can delete by calling Delete Label from the Labels API.
 *
 * Members are typically linked to contacts, and while they share a relationship, the member ID and contact ID are distinct identifiers.
 * Make sure to specify the contact ID when calling Unlabel Contact.
 */
export function unlabelContact(payload: object): RequestOptionsFactory<any> {
  function __unlabelContact({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.contacts.v4.contact',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.contacts.core.api.v4.ContactsServiceV4.UnlabelContact',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressContactsCoreApiV4ContactsServiceV4Url({
        protoPath: '/v4/contacts/{contactId}/labels',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'contact.createdDate' },
              { path: 'contact.updatedDate' },
              { path: 'contact.lastActivity.activityDate' },
              { path: 'contact.lastActivity.date' },
              { path: 'contact.picture.urlExpirationDate' },
              { path: 'contact.info.picture.image.urlExpirationDate' },
              { path: 'contact.memberInfo.signupDate' },
              {
                path: 'contact.memberInfo.profileInfo.photo.urlExpirationDate',
              },
              { path: 'contact.memberInfo.sessionInfo.lastWebLogin' },
              { path: 'contact.memberInfo.sessionInfo.lastMobileLogin' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'contact.info.addresses.items.address.geocode.latitude' },
              {
                path: 'contact.info.addresses.items.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __unlabelContact;
}

/** Retrieves a list of up to 1,000 contacts per request. */
export function listContacts(payload: object): RequestOptionsFactory<any> {
  function __listContacts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.contacts.v4.contact',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.contacts.core.api.v4.ContactsServiceV4.ListContacts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressContactsCoreApiV4ContactsServiceV4Url({
        protoPath: '/v4/contacts',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'contacts.createdDate' },
              { path: 'contacts.updatedDate' },
              { path: 'contacts.lastActivity.activityDate' },
              { path: 'contacts.lastActivity.date' },
              { path: 'contacts.picture.urlExpirationDate' },
              { path: 'contacts.info.picture.image.urlExpirationDate' },
              { path: 'contacts.memberInfo.signupDate' },
              {
                path: 'contacts.memberInfo.profileInfo.photo.urlExpirationDate',
              },
              { path: 'contacts.memberInfo.sessionInfo.lastWebLogin' },
              { path: 'contacts.memberInfo.sessionInfo.lastMobileLogin' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'contacts.info.addresses.items.address.geocode.latitude',
              },
              {
                path: 'contacts.info.addresses.items.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listContacts;
}

/**
 * Creates a query to retrieve a list of contacts.
 *
 * The `queryContacts()` function builds a query to retrieve a list of contacts and returns a `ContactsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
 *
 * You can refine the query by chaining `ContactsQueryBuilder` functions onto the query. `ContactsQueryBuilder` functions enable you to sort, filter, and control the results `queryContacts()` returns.
 *
 * `queryContacts()` runs with these `ContactsQueryBuilder` defaults, which you can override:
 *
 * - `skip(0)`
 * - `limit(50)`
 * - `descending("_createdDate")`
 *
 * The functions that are chained to `queryContacts()` are applied in the order they are called. For example, if you apply `ascending('info.company')` and then `descending('info.name.last')`, the results are sorted first by the company name, and then, if there are multiple results with the same company, the items are sorted by last name.
 */
export function queryContacts(payload: object): RequestOptionsFactory<any> {
  function __queryContacts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.contacts.v4.contact',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.contacts.core.api.v4.ContactsServiceV4.QueryContacts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressContactsCoreApiV4ContactsServiceV4Url({
        protoPath: '/v4/contacts/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'contacts.createdDate' },
              { path: 'contacts.updatedDate' },
              { path: 'contacts.lastActivity.activityDate' },
              { path: 'contacts.lastActivity.date' },
              { path: 'contacts.picture.urlExpirationDate' },
              { path: 'contacts.info.picture.image.urlExpirationDate' },
              { path: 'contacts.memberInfo.signupDate' },
              {
                path: 'contacts.memberInfo.profileInfo.photo.urlExpirationDate',
              },
              { path: 'contacts.memberInfo.sessionInfo.lastWebLogin' },
              { path: 'contacts.memberInfo.sessionInfo.lastMobileLogin' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'contacts.info.addresses.items.address.geocode.latitude',
              },
              {
                path: 'contacts.info.addresses.items.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryContacts;
}

/**
 * Lists facets from the site’s contact list.
 * Facets include labels and subscription statuses.
 */
export function listFacets(payload: object): RequestOptionsFactory<any> {
  function __listFacets({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.contacts.v4.contact',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.contacts.core.api.v4.ContactsServiceV4.ListFacets',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressContactsCoreApiV4ContactsServiceV4Url({
        protoPath: '/v4/contacts/facets',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __listFacets;
}

/**
 * Retrieves facets from the site’s contact list by filter.
 * Facets include labels and subscription statuses.
 */
export function queryFacets(payload: object): RequestOptionsFactory<any> {
  function __queryFacets({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.contacts.v4.contact',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.contacts.core.api.v4.ContactsServiceV4.QueryFacets',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressContactsCoreApiV4ContactsServiceV4Url({
        protoPath: '/v4/contacts/facets/query',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __queryFacets;
}

/**
 * Deletes multiple contacts.
 *
 * All contacts that meet the specified `filter` and `search` criteria are deleted.
 * The request should contain a `filter` value or a `search` value, or both.
 * To perform a dry run, call Query Contacts with the intended filter options.
 *
 * When this method is called, a bulk job is started and the job ID is returned.
 * The job might not complete right away, depending on its size.
 * The job's status can be retrieved with Get Bulk Job.
 *
 * The following errors might occur during the bulk processing and will appear in the bulk job:
 * - `CANNOT_DELETE_SITE_MEMBERS` - Contact is a site member and can't be deleted. Member must be deleted first.
 * - `CANNOT_DELETE_CONTACT_WITH_BILLING_SUBSCRIPTION` - Contact has a valid billing subscription and can't be deleted.
 * - `CANNOT_DELETE_MEMBER_OWNER_OR_CONTRIBUTOR` - Member is a Wix user and can't be deleted. This can happen only if the request indicated to delete the member.
 * - `FAILED_DELETE_CONTACT_AFTER_MEMBER_DELETION` - Member was deleted, but contact was not. This can happen only if the request indicated to delete the member.
 * - `FAILED_DELETE_CONTACT` - Contact could not be deleted.
 */
export function bulkDeleteContacts(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteContacts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.contacts.v4.contact',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.contacts.core.api.v4.ContactsServiceV4.BulkDeleteContacts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressContactsCoreApiV4ContactsServiceV4Url({
        protoPath: '/v4/bulk/contacts/delete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteContacts;
}

/**
 * Updates the specified properties for multiple contacts.
 * Fields that are included in fieldMask.paths are updated,
 * while all other fields stay the same.
 *
 * All contacts that meet the specified `filter` and `search` criteria are updated.
 * To perform a dry run, call Query Contacts with the intended filter options.
 *
 * When this method is used, a bulk job is started and the job ID is returned.
 * The job may not complete right away, depending on its size.
 * The job's status can be retrieved with Get Bulk Job.
 */
export function bulkUpdateContacts(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateContacts({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'info.addresses.items.address.geocode.latitude' },
          { path: 'info.addresses.items.address.geocode.longitude' },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'info.picture.image.urlExpirationDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.contacts.v4.contact',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.contacts.core.api.v4.ContactsServiceV4.BulkUpdateContacts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressContactsCoreApiV4ContactsServiceV4Url({
        protoPath: '/v4/bulk/contacts/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __bulkUpdateContacts;
}

/**
 * Adds and removes labels from multiple contacts.
 *
 * Labels are added to and removed from all contacts that meet the specified
 * `filter` and `search` criteria.
 * The request should specify a `filter` value, a `search` value, or both.
 * To perform a dry run, call Query Contacts with the intended filter options.
 *
 * When this method is used, a bulk job is started and the job ID is returned.
 * The job might not complete right away, depending on its size.
 * The job's status can be retrieved with Get Bulk Job.
 */
export function bulkLabelAndUnlabelContacts(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkLabelAndUnlabelContacts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.contacts.v4.contact',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.contacts.core.api.v4.ContactsServiceV4.BulkLabelAndUnlabelContacts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressContactsCoreApiV4ContactsServiceV4Url({
        protoPath: '/v4/bulk/contacts/add-remove-labels',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkLabelAndUnlabelContacts;
}

/**
 * Retrieves a contact.
 *
 * #### Getting Merged Contacts
 *
 * When a source contact is merged
 * with a target contact, the source contact is deleted.
 * When calling Get Contact for a merged contact,
 * you can use the source or target contact ID.
 * In both bases, the target contact is returned.
 *
 * This is supported only when calling Get Contact on merged contacts.
 * Previously deleted source contact IDs can't be used for any other method.
 */
export function getContact(payload: object): RequestOptionsFactory<any> {
  function __getContact({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.contacts.v4.contact',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.contacts.core.api.v4.ContactsServiceV4.GetContact',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressContactsCoreApiV4ContactsServiceV4Url({
        protoPath: '/v4/contacts/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'contact.createdDate' },
              { path: 'contact.updatedDate' },
              { path: 'contact.lastActivity.activityDate' },
              { path: 'contact.lastActivity.date' },
              { path: 'contact.picture.urlExpirationDate' },
              { path: 'contact.info.picture.image.urlExpirationDate' },
              { path: 'contact.memberInfo.signupDate' },
              {
                path: 'contact.memberInfo.profileInfo.photo.urlExpirationDate',
              },
              { path: 'contact.memberInfo.sessionInfo.lastWebLogin' },
              { path: 'contact.memberInfo.sessionInfo.lastMobileLogin' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'contact.info.addresses.items.address.geocode.latitude' },
              {
                path: 'contact.info.addresses.items.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getContact;
}
